import React, { FC, ReactNode, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import LocationInput from '@uikit/FormItems/LocationInput/LocationInput';
import { newInfoType } from '@src/types';
import { toast } from 'react-toastify';
import { getLocationAsText } from '@src/utils';
import { getDynamicValidationColorsGlobal } from '@uikit/FormItems/ControlledItems/helpers';

type TypeControlledLocationInput = {
  control?: any;
  rules?: any;
  name: string;
  inputId: string;
  placeholder?: string;
  info?: newInfoType;
  title?: string;
  required?: boolean;
  IconLeft?: FC | ReactNode;
  IconRight?: FC | ReactNode;
  change?: (e: any) => void;
  error?: boolean;
  onBlur?: (e: any) => void;
  disabled?: boolean;
  maxWidth?: string;
  defaultValue: string;
  isInModal?: boolean;
  setIsModalOpened?: (opened: boolean) => void;
  onPlaceSelect?: (place: any, index?: number) => void;
  dropdownClassName?: string;
  index?: number;
  tabIndex?: number;
  verified?: boolean;
  verifiedAddress?: boolean;
  dataTestId?: string;
  hideErrorWhenEmpty?: boolean;
};

export const ControlledLocationInput: FC<TypeControlledLocationInput> = ({
  control,
  rules,
  name,
  hideErrorWhenEmpty = false,
  ...rest
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const locationText =
          typeof field.value === 'string'
            ? field.value
            : getLocationAsText(field.value);
        const isEmpty = !locationText && !rest.defaultValue;
        const hasExternalError = !!rest.error;
        const hasValidationError = !!fieldState.error || hasExternalError;
        const shouldShowValidation = !hideErrorWhenEmpty || !isEmpty;
        const visibleError =
          shouldShowValidation && hasValidationError && !isEmpty;
        const validationDynamicClassName = getDynamicValidationColorsGlobal(
          { value: locationText || rest.defaultValue },
          visibleError
        );
        useEffect(() => {
          if (shouldShowValidation && fieldState.error) {
            const message = fieldState.error?.message;
            message && toast.error(message);
          }
        }, [fieldState.error, shouldShowValidation]);

        const { ref, ...fieldWithoutRef } = field;
        const verifiedAddress =
          rest.verifiedAddress ??
          (typeof field.value?.verifiedAddress === 'boolean'
            ? field.value.verifiedAddress
            : undefined);

        return (
          <LocationInput
            {...fieldWithoutRef}
            {...rest}
            required={rest.required || !!rules?.required}
            verifiedAddress={verifiedAddress}
            error={visibleError}
            // TODO check this part properly and then approve // Check onChange
            change={(e) => {
              field.onChange(e);
              rest.change?.(e);
            }}
            validationDynamicClassName={validationDynamicClassName}
          />
        );
      }}
    />
  );
};
